/**
 * Web Vitals Optimization Utilities
 * 
 * This module provides functions for optimizing and monitoring Core Web Vitals
 * including LCP (Largest Contentful Paint), CLS (Cumulative Layout Shift),
 * and FID (First Input Delay)
 */

/**
 * Initialize Web Vitals optimization
 * Call this function early in your application
 */
export const initWebVitalsOptimization = () => {
  // Only run in the browser
  if (typeof window === 'undefined') return;

  // Optimize LCP by preloading hero images
  optimizeLCP();

  // Prevent CLS by reserving space for content
  preventCLS();

  // Optimize FID by deferring non-critical work
  optimizeFID();

  // Monitor web vitals (optional) - only in production
  if (import.meta.env.PROD) {
    monitorWebVitals();
  }
};

/**
 * Optimize Largest Contentful Paint (LCP)
 * - LCP measures when the largest content element becomes visible
 * - Goal: < 2.5 seconds
 */
const optimizeLCP = () => {
  // Preload critical resources
  const preloadLinks = [
    // Add your critical resources (images, fonts, CSS) here
    // Format: { href: '/path/to/resource', as: 'image' or 'font' or 'style' }
  ];

  preloadLinks.forEach(({ href, as }) => {
    if (!href || !as) return;
    
    const link = document.createElement('link');
    link.rel = 'preload';
    link.href = href;
    link.as = as;
    
    if (as === 'font') {
      link.type = 'font/woff2';
      link.crossOrigin = 'anonymous';
    }
    
    document.head.appendChild(link);
  });

  // Set priority hints for the hero image
  const setImgPriorities = () => {
    const heroImages = document.querySelectorAll('.hero-image, .header-logo');
    heroImages.forEach((img) => {
      if (img.fetchPriority !== 'high') {
        img.fetchPriority = 'high';
      }
    });
  };

  // Set after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setImgPriorities);
  } else {
    setImgPriorities();
  }
};

/**
 * Prevent Cumulative Layout Shift (CLS)
 * - CLS measures unexpected layout shifts
 * - Goal: < 0.1
 */
const preventCLS = () => {
  // Set explicit dimensions for media elements
  const addExplicitDimensions = () => {
    const mediaElements = document.querySelectorAll('img, video');
    
    mediaElements.forEach((el) => {
      // Only handle elements without explicit dimensions
      if (!el.hasAttribute('width') || !el.hasAttribute('height')) {
        // For images that are loaded, get natural dimensions
        if (el.tagName === 'IMG' && el.complete) {
          if (el.naturalWidth && el.naturalHeight) {
            el.setAttribute('width', el.naturalWidth);
            el.setAttribute('height', el.naturalHeight);
            
            // Add aspect ratio to preserve space
            el.style.aspectRatio = `${el.naturalWidth} / ${el.naturalHeight}`;
          }
        }
        // For videos and unloaded images, set default dimensions
        else {
          // Set default placeholder dimensions if missing
          if (!el.hasAttribute('width')) el.setAttribute('width', 640);
          if (!el.hasAttribute('height')) el.setAttribute('height', 360);
        }
      }
    });
  };

  // Apply after DOM is loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', addExplicitDimensions);
  } else {
    addExplicitDimensions();
  }

  // Add CSS to prevent layout shifts for fonts
  const style = document.createElement('style');
  style.textContent = `
    .font-size-stable {
      font-display: optional;
    }
  `;
  document.head.appendChild(style);
};

/**
 * Optimize First Input Delay (FID)
 * - FID measures responsiveness to user interactions
 * - Goal: < 100ms
 */
const optimizeFID = () => {
  // Break up long tasks
  const scheduleTasks = () => {
    // Identify and split up tasks that might block the main thread
    
    // Example: Defer non-critical initialization
    setTimeout(() => {
      // Non-critical initialization code here
      
      // For example, initializing analytics, non-visible components, etc.
    }, 1000);
  };

  // Execute after the page is interactive
  if (document.readyState === 'complete') {
    scheduleTasks();
  } else {
    window.addEventListener('load', scheduleTasks);
  }
};

/**
 * Monitor Web Vitals
 * Reports Web Vitals metrics to your analytics platform
 */
const monitorWebVitals = async () => {
  try {
    // Only try to import web-vitals in production to prevent errors during development
    if (import.meta.env.PROD) {
      // Dynamically import web-vitals library (you need to install it)
      const { getCLS, getFID, getLCP, getFCP, getTTFB } = await import('web-vitals');
      
      const reportWebVital = ({ name, value, id }) => {
        // Example: sending to Google Analytics 4
        if (window.gtag) {
          window.gtag('event', name, {
            value: Math.round(name === 'CLS' ? value * 1000 : value),
            metric_id: id,
            metric_value: value,
            metric_delta: 0, // Calculate delta if tracking improvement
          });
        }
      };
      
      // Monitor each web vital
      getCLS(reportWebVital);
      getFID(reportWebVital);
      getLCP(reportWebVital);
      getFCP(reportWebVital);
      getTTFB(reportWebVital);
    }
  } catch (error) {
    console.error('Failed to load web-vitals', error);
  }
}; 